import { createStore } from 'vuex';
import { BookingAPI } from '@/widgets/booking/api';
import { bookingWidgetFeatures } from '@/widgets/booking/config/features';
import { hasRuntimeClubId, logMissingClubId } from '@/widgets/booking/config/runtime';
import { pickPreferredCurrency } from '@/widgets/booking/utils/currency';
const showWithTrainers = bookingWidgetFeatures.showWithTrainers;

function formatDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function hasSameItems(first, second) {
    return first.length === second.length && first.every((item, index) => item === second[index]);
}

function findMatchingCourtDuration(courts, selection) {
    if (!selection) {
        return null;
    }

    for (const court of courts) {
        if (court.id !== selection.courtId) {
            continue;
        }

        const duration = court.durations.find(item => {
            return item.minutes === selection.duration
                && item.serviceId === selection.serviceId
                && item.trainingId === selection.trainingId;
        });

        if (duration) {
            return duration;
        }
    }

    return null;
}

export default createStore({
    state: {
        user: null,
        club: null,
        branches: [],
        directions: [],
        availableDays: [],
        bookingWidgetCountWeek: 0,
        trainers: [],
        timeSlots: [],
        courts: [],
        selectedDirections: [],
        selectedDate: undefined,
        selectedTrainerId: undefined,
        trainersEnabled: false,
        selectedSlotId: undefined,
        selectedCourtDuration: undefined,
        showThankYouModal: false
    },
    getters: {
        availableDatesStrings(state) {
            return state.availableDays
                .filter(day => day.hasSlots)
                .map(day => day.date);
        },
        selectedTrainer(state) {
            if (!state.selectedTrainerId)
                return null;
            return state.trainers.find(t => t.id === state.selectedTrainerId) || null;
        },
        selectedTrainerFullName(state, getters) {
            if (!getters.selectedTrainer)
                return undefined;
            const trainer = getters.selectedTrainer;
            const firstNameInitial = trainer.firstName ? trainer.firstName.charAt(0) + '.' : '';
            return `${trainer.lastName} ${firstNameInitial}`;
        },
        selectedSlot(state) {
            if (!state.selectedSlotId)
                return null;
            return state.timeSlots.find(s => s.id === state.selectedSlotId) || null;
        },
        selectedSlotTime(state, getters) {
            return getters.selectedSlot?.time;
        },
        selectedCourt(state) {
            if (!state.selectedCourtDuration)
                return null;
            return state.courts.find(c => c.id === state.selectedCourtDuration?.courtId) || null;
        },
        selectedCourtName(state, getters) {
            return getters.selectedCourt?.name;
        }
    },
    mutations: {
        SET_USER(state, user) {
            state.user = user;
        },
        SET_CLUB(state, club) {
            state.club = club;
        },
        SET_BRANCHES(state, branches) {
            state.branches = branches;
        },
        SET_DIRECTIONS(state, directions) {
            state.directions = directions;
        },
        SET_AVAILABLE_DAYS(state, days) {
            state.availableDays = days;
        },
        SET_BOOKING_WIDGET_COUNT_WEEK(state, countWeek) {
            state.bookingWidgetCountWeek = Number(countWeek) || 0;
        },
        SET_TRAINERS(state, trainers) {
            state.trainers = trainers;
        },
        SET_TIME_SLOTS(state, slots) {
            state.timeSlots = slots;
        },
        SET_COURTS(state, courts) {
            state.courts = courts;
        },
        SET_SELECTED_DIRECTIONS(state, directions) {
            state.selectedDirections = directions;
        },
        SET_SELECTED_DATE(state, date) {
            state.selectedDate = date;
        },
        SET_SELECTED_TRAINER_ID(state, trainerId) {
            state.selectedTrainerId = trainerId;
        },
        SET_TRAINERS_ENABLED(state, enabled) {
            state.trainersEnabled = enabled;
        },
        SET_SELECTED_SLOT_ID(state, slotId) {
            state.selectedSlotId = slotId;
        },
        SET_SELECTED_COURT_DURATION(state, selection) {
            state.selectedCourtDuration = selection;
        },
        SET_SHOW_THANK_YOU_MODAL(state, show) {
            state.showThankYouModal = show;
        },
        RESET_SELECTIONS(state) {
            state.selectedDirections = [];
            state.selectedDate = undefined;
            state.selectedTrainerId = undefined;
            state.trainersEnabled = false;
            state.selectedSlotId = undefined;
            state.selectedCourtDuration = undefined;
        }
    },
    actions: {
        async loadInitialData({ state, commit, dispatch }) {
            if (!hasRuntimeClubId()) {
                logMissingClubId();
                commit('SET_USER', null);
                commit('SET_CLUB', null);
                commit('SET_BRANCHES', []);
                commit('SET_DIRECTIONS', []);
                commit('SET_AVAILABLE_DAYS', []);
                commit('SET_TRAINERS', []);
                commit('SET_TIME_SLOTS', []);
                commit('SET_COURTS', []);
                commit('SET_SELECTED_TRAINER_ID', undefined);
                commit('SET_TRAINERS_ENABLED', false);
                return;
            }
            try {
                const [user, club, branches, directions] = await Promise.all([
                    BookingAPI.getUser(),
                    BookingAPI.getClub(),
                    BookingAPI.getBranches(),
                    BookingAPI.getDirections()
                ]);
                const trainers = showWithTrainers ? await BookingAPI.getTrainers() : [];
                commit('SET_USER', user);
                commit('SET_CLUB', club);
                commit('SET_BRANCHES', branches);
                const directionIds = directions.map(direction => direction.id);
                const selectedDirections = state.selectedDirections.filter(directionId => directionIds.includes(directionId));
                const nextSelectedDirections = selectedDirections.length ? selectedDirections : (directions[0]?.id ? [directions[0].id] : []);
                commit('SET_DIRECTIONS', directions);
                if (!hasSameItems(state.selectedDirections, nextSelectedDirections)) {
                    commit('SET_SELECTED_DIRECTIONS', nextSelectedDirections);
                }
                commit('SET_AVAILABLE_DAYS', []);
                commit('SET_TRAINERS', trainers);
                commit('SET_SELECTED_TRAINER_ID', undefined);
                commit('SET_TRAINERS_ENABLED', false);
                await dispatch('refreshSelectionData');
            }
            catch (error) {
                console.error('[booking-widget] Failed to load initial data', error);
                commit('SET_USER', null);
                commit('SET_CLUB', null);
                commit('SET_BRANCHES', []);
                commit('SET_DIRECTIONS', []);
                commit('SET_AVAILABLE_DAYS', []);
                commit('SET_TRAINERS', []);
                commit('SET_SELECTED_TRAINER_ID', undefined);
                commit('SET_TRAINERS_ENABLED', false);
            }
        },
        async refreshSelectionData({ state, commit, dispatch }) {
            if (state.selectedDirections.length === 0) {
                commit('SET_AVAILABLE_DAYS', []);
                commit('SET_TIME_SLOTS', []);
                commit('SET_COURTS', []);
                return;
            }
            await dispatch('loadAvailableDays');
            dispatch('selectDefaultDate');
            if (!state.selectedDate) {
                commit('SET_TIME_SLOTS', []);
                commit('SET_COURTS', []);
                return;
            }
            if (showWithTrainers) {
                await dispatch('loadTrainers');
            }
            else {
                commit('SET_TRAINERS', []);
                commit('SET_SELECTED_TRAINER_ID', undefined);
                commit('SET_TRAINERS_ENABLED', false);
            }
            await dispatch('loadTimeSlots');
            const hasSelectedSlot = state.timeSlots.some((slot) => slot.id === state.selectedSlotId);
            if (!hasSelectedSlot) {
                commit('SET_SELECTED_SLOT_ID', undefined);
                commit('SET_SELECTED_COURT_DURATION', undefined);
                commit('SET_COURTS', []);
                return;
            }
            await dispatch('loadCourts');
            const selection = state.selectedCourtDuration;
            if (!selection) {
                return;
            }
            const matchingDuration = findMatchingCourtDuration(state.courts, selection);
            if (!matchingDuration) {
                commit('SET_SELECTED_COURT_DURATION', undefined);
                return;
            }
            commit('SET_SELECTED_COURT_DURATION', {
                ...selection,
                price: matchingDuration.price,
                currency: pickPreferredCurrency(
                    pickPreferredCurrency(matchingDuration.currency, selection.currency),
                    state.club?.currency
                ),
                serviceId: matchingDuration.serviceId,
                trainingId: matchingDuration.trainingId
            });
        },
        async loadAvailableDays({ state, commit }) {
            if (state.selectedDirections.length === 0) {
                commit('SET_AVAILABLE_DAYS', []);
                return;
            }
            const filters = {
                directionIds: state.selectedDirections.length > 0 ? state.selectedDirections : undefined,
                countWeek: state.bookingWidgetCountWeek
            };
            const days = await BookingAPI.getAvailableDays(filters);
            commit('SET_AVAILABLE_DAYS', days);
        },
        async loadTrainers({ state, commit }) {
            if (!showWithTrainers) {
                commit('SET_TRAINERS', []);
                commit('SET_SELECTED_TRAINER_ID', undefined);
                commit('SET_TRAINERS_ENABLED', false);
                return;
            }
            if (!state.selectedDate) {
                commit('SET_TRAINERS', []);
                return;
            }
            const filters = {
                directionIds: state.selectedDirections.length > 0 ? state.selectedDirections : undefined,
                date: state.selectedDate,
                trainerId: showWithTrainers && state.trainersEnabled ? state.selectedTrainerId : undefined
            };
            const trainers = await BookingAPI.getTrainers(filters);
            commit('SET_TRAINERS', trainers);
        },
        async loadTimeSlots({ state, commit }) {
            if (!state.selectedDate || state.selectedDirections.length === 0) {
                commit('SET_TIME_SLOTS', []);
                return;
            }
            const filters = {
                directionIds: state.selectedDirections.length > 0 ? state.selectedDirections : undefined,
                date: state.selectedDate,
                trainerId: showWithTrainers && state.trainersEnabled ? state.selectedTrainerId : undefined
            };
            const slots = await BookingAPI.getTimeSlots(filters);
            commit('SET_TIME_SLOTS', slots);
        },
        async loadCourts({ state, commit }) {
            if (!state.selectedDate || !state.selectedSlotId || state.selectedDirections.length === 0) {
                commit('SET_COURTS', []);
                return;
            }
            const filters = {
                directionIds: state.selectedDirections.length > 0 ? state.selectedDirections : undefined,
                date: state.selectedDate,
                time: state.timeSlots.find(slot => slot.id === state.selectedSlotId)?.time
            };
            const courts = await BookingAPI.getCourtsWithDurations(filters);
            commit('SET_COURTS', courts);
        },
        selectDefaultDate({ state, commit }) {
            if (state.selectedDate) {
                return;
            }
            commit('SET_SELECTED_DATE', formatDate(new Date()));
        },
        async onDirectionsChange({ dispatch, commit, state }) {
            await dispatch('loadAvailableDays');
            dispatch('selectDefaultDate');
            if (state.selectedDate) {
                if (showWithTrainers) {
                    await dispatch('loadTrainers');
                }
                else {
                    commit('SET_TRAINERS', []);
                    commit('SET_SELECTED_TRAINER_ID', undefined);
                    commit('SET_TRAINERS_ENABLED', false);
                }
                await dispatch('loadTimeSlots');
            }
            if (state.selectedSlotId) {
                await dispatch('loadCourts');
            }
            // Reset selections
            commit('SET_SELECTED_SLOT_ID', undefined);
            commit('SET_SELECTED_COURT_DURATION', undefined);
        },
        async onDateChange({ dispatch, commit, state }) {
            commit('SET_TIME_SLOTS', []);
            commit('SET_COURTS', []);
            commit('SET_SELECTED_SLOT_ID', undefined);
            commit('SET_SELECTED_COURT_DURATION', undefined);

            if (state.selectedDate) {
                if (showWithTrainers) {
                    await dispatch('loadTrainers');
                }
                else {
                    commit('SET_TRAINERS', []);
                    commit('SET_SELECTED_TRAINER_ID', undefined);
                    commit('SET_TRAINERS_ENABLED', false);
                }
                await dispatch('loadTimeSlots');
            }
            else {
                commit('SET_TRAINERS', []);
                commit('SET_SELECTED_TRAINER_ID', undefined);
            }
        },
        async onTrainerChange({ dispatch, commit }) {
            await dispatch('loadTimeSlots');
            // Reset selections
            commit('SET_SELECTED_SLOT_ID', undefined);
            commit('SET_SELECTED_COURT_DURATION', undefined);
        },
        async onSlotChange({ dispatch, commit, state }) {
            if (state.selectedSlotId) {
                await dispatch('loadCourts');
            }
            else {
                commit('SET_COURTS', []);
            }
            // Reset court selection
            commit('SET_SELECTED_COURT_DURATION', undefined);
        },
        async bookSlot({ state, commit }) {
            if (!state.selectedDate || !state.selectedSlotId || !state.selectedCourtDuration?.serviceId || !state.selectedCourtDuration.trainingId) {
                return;
            }
            try {
                const bookingData = {
                    training_id: state.selectedCourtDuration.trainingId,
                    place_id: state.selectedCourtDuration.courtId,
                    service_id: state.selectedCourtDuration.serviceId,
                    date: state.selectedDate,
                    time: state.timeSlots.find(slot => slot.id === state.selectedSlotId)?.time || '',
                    trainer_id: showWithTrainers && state.trainersEnabled ? state.selectedTrainerId : undefined
                };
                const result = await BookingAPI.bookSlot(bookingData);
                if (result.success) {
                    commit('SET_SHOW_THANK_YOU_MODAL', true);
                }
            }
            catch (error) {
                console.error('Booking error:', error);
            }
        },
        async closeThankYouModal({ commit, dispatch }) {
            commit('SET_SHOW_THANK_YOU_MODAL', false);
            commit('RESET_SELECTIONS');
            // Reload initial data
            await dispatch('loadInitialData');
        }
    }
});
